import React from "react";
import { WarningOctagon, XCircle } from "@phosphor-icons/react";
import { isPaidCancellation } from "@/lib/orderCancellation";

/**
 * Prominent red banner rendered at the top of OrderDetail when an order is
 * cancelled. Shows external_status, payment_status, notes, and an explicit
 * "do not pick / build / ship / print" footer so warehouse staff don't
 * accidentally fulfill a refunded order.
 */
export default function CancelledBanner({ order }) {
  if (!order || order.status !== "cancelled") return null;
  const refund = isPaidCancellation(order);
  const notes = (order.notes || "").trim();
  const ext = order.external_status || "cancelled";
  const pay = order.payment_status || null;

  return (
    <div
      data-testid="cancelled-banner"
      className="rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-600 overflow-hidden"
    >
      <div className="flex items-start gap-3 p-5">
        <div className="shrink-0 w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center">
          {refund ? <WarningOctagon size={22} weight="fill" /> : <XCircle size={22} weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-2xl uppercase tracking-wide text-red-700 dark:text-red-300">
            {refund ? "Order cancelled · refund needed" : "Order cancelled"}
          </div>
          <div className="mt-2 text-sm text-red-900 dark:text-red-200 space-y-1">
            <div>
              <span className="font-bold uppercase tracking-widest text-[10px] mr-2">Upstream</span>
              <span className="font-mono">{ext}</span>
            </div>
            {pay && (
              <div>
                <span className="font-bold uppercase tracking-widest text-[10px] mr-2">Payment</span>
                <span className={`font-mono ${refund ? "font-bold underline" : ""}`}>{pay}</span>
                {refund && (
                  <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-red-600 text-white">
                    refund owed
                  </span>
                )}
              </div>
            )}
            {notes && (
              <div className="pt-1.5">
                <span className="font-bold uppercase tracking-widest text-[10px] mr-2">Notes</span>
                <span className="italic">"{notes}"</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-red-600 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em]">
        Do not pick, build, ship, or print labels for this order.
      </div>
    </div>
  );
}
